import { computed } from 'vue'

import { appBranding } from '@/lib/features'

export function useBranding() {
  // Extract common active and config check
  const activeBrandingConfig = computed(() => (appBranding?.isActive ? appBranding.config : null))

  const isValidUrl = (url: string): boolean => URL.canParse(url)

  const platformAssetUrl = computed(() => activeBrandingConfig.value?.assetUrl ?? null)

  const platform = computed(() => activeBrandingConfig.value?.platform ?? null)

  const isPlatformMarkero = computed(() => platform.value === 'markero')

  const legalCompanyName = computed(() => activeBrandingConfig.value?.legalCompanyName ?? null)

  const readablePlatformName = computed(
    () => activeBrandingConfig.value?.readablePlatformName ?? null
  )

  const dataPrivacyUrl = computed(() => {
    const url = activeBrandingConfig.value?.dataPrivacyUrl
    return url && isValidUrl(url) ? url : null
  })

  const publicDataPrivacyUrl = computed(() => activeBrandingConfig.value?.publicDataPrivacyUrl)

  const publicTOCandLicenceUrl = computed(() => activeBrandingConfig.value?.publicTOCandLicenceUrl)

  const imprintUrl = computed(() => activeBrandingConfig.value?.imprintUrl)

  const primaryColor = computed(() => activeBrandingConfig.value?.primary)

  // Use an enum or const object for better type safety and readability
  const DEFAULT_COLORS = {
    PRIMARY: '#07BEB8',
    LOGIN_BACKGROUND: '#0896c9',
  } as const

  const isValidHexColor = (color: string | null | undefined): boolean =>
    color ? /^#[0-9A-F]{6}$/i.test(color) : false

  const createColorWithOpacity = (color: string, opacity: number): string => {
    const hex = Math.round(opacity * 255)
      .toString(16)
      .padStart(2, '0')
    return `${color}${hex}`
  }

  const gradientColors = computed(() => {
    const config = activeBrandingConfig.value
    return {
      start: isValidHexColor(config?.primary) ? config.primary : DEFAULT_COLORS.PRIMARY,
      end: isValidHexColor(config?.loginBackgroundColor)
        ? config.loginBackgroundColor
        : DEFAULT_COLORS.LOGIN_BACKGROUND,
    }
  })

  const staticGradientColors = computed(() => {
    const config = activeBrandingConfig.value
    return {
      start: createColorWithOpacity(
        isValidHexColor(config?.primary) ? config.primary : DEFAULT_COLORS.PRIMARY,
        0.6
      ),
      end: createColorWithOpacity(
        isValidHexColor(config?.loginBackgroundColor)
          ? config.loginBackgroundColor
          : DEFAULT_COLORS.LOGIN_BACKGROUND,
        0.6
      ),
    }
  })

  return {
    platformAssetUrl,
    platform,
    isPlatformMarkero,
    legalCompanyName,
    readablePlatformName,
    dataPrivacyUrl,
    publicDataPrivacyUrl,
    publicTOCandLicenceUrl,
    imprintUrl,
    primaryColor,
    gradientColors,
    staticGradientColors,
  }
}
